import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';

interface ShopifyProduct {
  id: string;
  title: string;
  handle: string;
  description: string;
  image: string;
  price: number;
  currencyCode: string;
  availableForSale: boolean;
}

interface HeaderProps {
  cartCount?: number;
  onCartClick?: () => void;
}

export default function Header({ cartCount = 0, onCartClick }: HeaderProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProductsOpen, setIsProductsOpen] = useState(false);
  const [isShirtsOpen, setIsShirtsOpen] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [featuredProducts, setFeaturedProducts] = useState<ShopifyProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [cartItems, setCartItems] = useState<any[]>([]);
  const [cartCountState, setCartCount] = useState(0);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const cartRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsProductsOpen(false);
      }
      if (cartRef.current && !cartRef.current.contains(event.target as Node)) {
        setIsCartOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isProductsOpen && featuredProducts.length === 0) {
      fetchFeaturedProducts();
    }
  }, [isProductsOpen]);

  useEffect(() => {
    // Load cart from localStorage
    loadCart();
    
    // Listen for cart updates
    const handleCartUpdate = () => {
      loadCart();
    };
    
    window.addEventListener('cartUpdated', handleCartUpdate);
    return () => window.removeEventListener('cartUpdated', handleCartUpdate);
  }, []);

  const loadCart = () => {
    const cart = JSON.parse(localStorage.getItem('cart') || '[]');
    setCartItems(cart);
    const count = cart.reduce((sum: number, item: any) => sum + item.quantity, 0);
    setCartCount(count);
  };

  const updateCartItemQuantity = (itemId: string, newQuantity: number) => {
    const cart = JSON.parse(localStorage.getItem('cart') || '[]');
    const updatedCart = cart.map((item: any) => 
      item.id === itemId ? { ...item, quantity: Math.max(1, newQuantity) } : item
    );
    localStorage.setItem('cart', JSON.stringify(updatedCart));
    loadCart();
  };

  const removeCartItem = (itemId: string) => {
    const cart = JSON.parse(localStorage.getItem('cart') || '[]');
    const updatedCart = cart.filter((item: any) => item.id !== itemId);
    localStorage.setItem('cart', JSON.stringify(updatedCart));
    loadCart();
  };

  const clearCart = () => {
    localStorage.setItem('cart', '[]');
    loadCart();
  };

  const cartTotal = cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);

  const fetchFeaturedProducts = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('get-featured-products');
      
      if (error) throw error;
      
      if (data?.products) {
        setFeaturedProducts(data.products);
      }
    } catch (error) {
      console.error('Error fetching featured products:', error);
    } finally {
      setLoading(false);
    }
  };

  const categories = [
    { 
      name: 'Shirts', 
      path: '/products?category=shirts',
      hasSubmenu: true,
      submenu: [
        { name: 'Short Sleeve', path: '/products?category=shirts&type=short-sleeve' },
        { name: 'Long Sleeve', path: '/products?category=longsleeves' }
      ]
    },
    { name: 'Shorts', path: '/products?category=shorts' },
    { name: 'Jackets', path: '/products?category=jackets' },
    { name: 'Pants', path: '/products?category=pants' },
    { name: 'Hats', path: '/products?category=hats' },
    { name: 'Drinkware', path: '/products?category=drinkware' },
  ];

  return (
    <>
      {/* Main Header - Always visible at top with cyan background */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-cyan-600 shadow-md">
        <div className="w-full px-6 lg:px-12">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center z-10">
              <img 
                src="https://static.readdy.ai/image/2733261fde4eb6ca5d5b92336d650a8a/30692f87ee96eca30e78f5feb62e78d5.png" 
                alt="AQUATEK" 
                className="h-8"
              />
            </Link>

            {/* Centered Navigation */}
            <nav className="hidden lg:flex items-center justify-center absolute left-1/2 -translate-x-1/2 space-x-10">
              <Link 
                to="/" 
                className="font-medium transition-colors cursor-pointer text-sm uppercase tracking-wider text-white hover:text-gray-900"
              >
                Home
              </Link>
              
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setIsProductsOpen(!isProductsOpen)}
                  className="font-medium transition-colors cursor-pointer flex items-center text-sm uppercase tracking-wider text-white hover:text-gray-900"
                >
                  Products
                  <i className={`ri-arrow-down-s-line ml-1 w-4 h-4 flex items-center justify-center transition-transform ${isProductsOpen ? 'rotate-180' : ''}`}></i>
                </button>

                {isProductsOpen && (
                  <div className="absolute top-full left-1/2 -translate-x-1/2 mt-4 w-[900px] bg-white rounded-lg shadow-2xl overflow-hidden border border-gray-100">
                    <div className="grid grid-cols-3 divide-x divide-gray-100">
                      {/* Categories Section */}
                      <div className="p-8 bg-gray-50">
                        <h3 className="text-xs font-bold text-gray-900 mb-6 uppercase tracking-widest">
                          Shop by Category
                        </h3>
                        <ul className="space-y-1">
                          {categories.map((category) => (
                            <li key={category.name}>
                              {category.hasSubmenu ? (
                                <div className="relative group">
                                  <div className="flex items-center justify-between px-4 py-3 text-gray-700 hover:bg-white hover:text-cyan-600 rounded-md transition-all cursor-pointer font-medium">
                                    <span>{category.name}</span>
                                    <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center"></i>
                                  </div>
                                  <div className="ml-4 mt-1 space-y-1">
                                    {category.submenu?.map((sub) => (
                                      <Link
                                        key={sub.name}
                                        to={sub.path}
                                        onClick={() => setIsProductsOpen(false)}
                                        className="block px-4 py-2 text-sm text-gray-600 hover:bg-white hover:text-cyan-600 rounded-md transition-all cursor-pointer"
                                      >
                                        {sub.name}
                                      </Link>
                                    ))}
                                  </div>
                                </div>
                              ) : (
                                <Link
                                  to={category.path}
                                  onClick={() => setIsProductsOpen(false)}
                                  className="block px-4 py-3 text-gray-700 hover:bg-white hover:text-cyan-600 rounded-md transition-all cursor-pointer font-medium"
                                >
                                  {category.name}
                                </Link>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Featured Products Section */}
                      <div className="col-span-2 p-8">
                        <h3 className="text-xs font-bold text-gray-900 mb-6 uppercase tracking-widest">
                          Featured Products
                        </h3>
                        
                        {loading ? (
                          <div className="flex items-center justify-center h-64">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
                          </div>
                        ) : (
                          <>
                            <div className="grid grid-cols-2 gap-6 mb-6">
                              {featuredProducts.slice(0, 4).map((product) => (
                                <Link
                                  key={product.id}
                                  to={`/product/${product.handle}`}
                                  onClick={() => setIsProductsOpen(false)}
                                  className="group cursor-pointer"
                                >
                                  <div className="bg-white rounded-lg overflow-hidden border border-gray-100 hover:border-cyan-400 hover:shadow-xl transition-all">
                                    <div className="aspect-square overflow-hidden bg-gray-50">
                                      <img
                                        src={product.image}
                                        alt={product.title}
                                        className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                                      />
                                    </div>
                                    <div className="p-4">
                                      <h4 className="text-sm font-semibold text-gray-900 mb-2 line-clamp-1 group-hover:text-cyan-600 transition-colors">
                                        {product.title}
                                      </h4>
                                      <div className="flex items-center justify-between">
                                        <span className="text-lg font-bold text-cyan-600">
                                          ${product.price.toFixed(2)}
                                        </span>
                                        {!product.availableForSale && (
                                          <span className="text-xs text-red-600 font-medium">
                                            Out of Stock
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </Link>
                              ))}
                            </div>

                            <Link
                              to="/products"
                              onClick={() => setIsProductsOpen(false)}
                              className="block w-full text-center py-3 bg-cyan-600 text-white font-semibold rounded-md hover:bg-cyan-700 transition-all cursor-pointer whitespace-nowrap text-sm uppercase tracking-wider"
                            >
                              View All Products
                            </Link>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <a 
                href="#about" 
                className="font-medium transition-colors cursor-pointer text-sm uppercase tracking-wider text-white hover:text-gray-900"
              >
                About
              </a>
              <a 
                href="#contact" 
                className="font-medium transition-colors cursor-pointer text-sm uppercase tracking-wider text-white hover:text-gray-900"
              >
                Contact
              </a>
            </nav>

            {/* Right Side Icons */}
            <div className="hidden lg:flex items-center space-x-6">
              <button className="transition-colors cursor-pointer text-white hover:text-gray-900">
                <i className="ri-search-line text-xl w-5 h-5 flex items-center justify-center"></i>
              </button>
              
              <button className="transition-colors cursor-pointer text-white hover:text-gray-900">
                <i className="ri-user-line text-xl w-5 h-5 flex items-center justify-center"></i>
              </button>

              <button
                onClick={() => setIsCartOpen(!isCartOpen)}
                className="relative transition-colors cursor-pointer text-white hover:text-gray-900"
              >
                <i className="ri-shopping-cart-line text-xl w-5 h-5 flex items-center justify-center"></i>
                {cartCountState > 0 && (
                  <span className="absolute -top-2 -right-2 bg-white text-cyan-600 text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                    {cartCountState}
                  </span>
                )}
              </button>
            </div>

            {/* Mobile Menu Button */}
            <div className="flex items-center space-x-4 lg:hidden">
              <button
                onClick={() => setIsCartOpen(!isCartOpen)}
                className="relative transition-colors cursor-pointer text-white hover:text-gray-900"
              >
                <i className="ri-shopping-cart-line text-xl w-5 h-5 flex items-center justify-center"></i>
                {cartCountState > 0 && (
                  <span className="absolute -top-2 -right-2 bg-white text-cyan-600 text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                    {cartCountState}
                  </span>
                )}
              </button>

              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="w-10 h-10 flex items-center justify-center cursor-pointer text-white"
              >
                <i className={`${isMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="lg:hidden bg-cyan-600 border-t border-cyan-500 shadow-lg">
            <nav className="px-6 py-4 space-y-1">
              <Link
                to="/"
                onClick={() => setIsMenuOpen(false)}
                className="block px-4 py-3 text-white hover:bg-cyan-700 rounded-md transition-colors cursor-pointer font-medium"
              >
                Home
              </Link>
              
              <div>
                <button
                  onClick={() => setIsProductsOpen(!isProductsOpen)}
                  className="w-full flex items-center justify-between px-4 py-3 text-white hover:bg-cyan-700 rounded-md transition-colors cursor-pointer font-medium"
                >
                  <span>Products</span>
                  <i className={`ri-arrow-down-s-line w-4 h-4 flex items-center justify-center transition-transform ${isProductsOpen ? 'rotate-180' : ''}`}></i>
                </button>
                
                {isProductsOpen && (
                  <div className="mt-1 ml-4 space-y-1">
                    {categories.map((category) => (
                      <div key={category.name}>
                        {category.hasSubmenu ? (
                          <>
                            <button
                              onClick={() => setIsShirtsOpen(!isShirtsOpen)}
                              className="w-full flex items-center justify-between px-4 py-2 text-gray-700 hover:bg-gray-50 hover:text-cyan-600 rounded-md transition-colors cursor-pointer"
                            >
                              <span>{category.name}</span>
                              <i className={`ri-arrow-down-s-line w-4 h-4 flex items-center justify-center transition-transform ${isShirtsOpen ? 'rotate-180' : ''}`}></i>
                            </button>
                            {isShirtsOpen && (
                              <div className="ml-4 mt-1 space-y-1">
                                {category.submenu?.map((sub) => (
                                  <Link
                                    key={sub.name}
                                    to={sub.path}
                                    onClick={() => {
                                      setIsMenuOpen(false);
                                      setIsProductsOpen(false);
                                      setIsShirtsOpen(false);
                                    }}
                                    className="block px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 hover:text-cyan-600 rounded-md transition-colors cursor-pointer"
                                  >
                                    {sub.name}
                                  </Link>
                                ))}
                              </div>
                            )}
                          </>
                        ) : (
                          <Link
                            to={category.path}
                            onClick={() => {
                              setIsMenuOpen(false);
                              setIsProductsOpen(false);
                            }}
                            className="block px-4 py-2 text-gray-700 hover:bg-gray-50 hover:text-cyan-600 rounded-md transition-colors cursor-pointer"
                          >
                            {category.name}
                          </Link>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <a
                href="#about"
                onClick={() => setIsMenuOpen(false)}
                className="block px-4 py-3 text-white hover:bg-cyan-700 rounded-md transition-colors cursor-pointer font-medium"
              >
                About
              </a>
              <a
                href="#contact"
                onClick={() => setIsMenuOpen(false)}
                className="block px-4 py-3 text-white hover:bg-cyan-700 rounded-md transition-colors cursor-pointer font-medium"
              >
                Contact
              </a>
            </nav>
          </div>
        )}

        {/* Cart Sidebar */}
        {isCartOpen && (
          <>
            <div 
              className="fixed inset-0 bg-black/50 z-40"
              onClick={() => setIsCartOpen(false)}
            ></div>
            
            <div 
              ref={cartRef}
              className="fixed top-0 right-0 h-full w-full md:w-96 bg-white shadow-2xl z-50 flex flex-col"
            >
              {/* Cart Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <h2 className="text-xl font-bold text-gray-900 uppercase tracking-wider">Shopping Cart</h2>
                <button
                  onClick={() => setIsCartOpen(false)}
                  className="text-gray-500 hover:text-gray-700 cursor-pointer"
                >
                  <i className="ri-close-line text-2xl w-6 h-6 flex items-center justify-center"></i>
                </button>
              </div>

              {/* Cart Items */}
              <div className="flex-1 overflow-y-auto p-6">
                {cartItems.length === 0 ? (
                  <div className="text-center py-12">
                    <i className="ri-shopping-cart-line text-6xl text-gray-300 mb-4 w-16 h-16 flex items-center justify-center mx-auto"></i>
                    <p className="text-gray-500 text-lg mb-6">Your cart is empty</p>
                    <Link
                      to="/products"
                      onClick={() => setIsCartOpen(false)}
                      className="inline-block bg-cyan-600 text-white px-6 py-3 rounded-md hover:bg-cyan-700 transition-colors cursor-pointer whitespace-nowrap font-semibold text-sm uppercase tracking-wider"
                    >
                      Continue Shopping
                    </Link>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {cartItems.map((item) => (
                      <div key={item.id} className="flex space-x-4 border-b border-gray-200 pb-4">
                        <img
                          src={item.image}
                          alt={item.title}
                          className="w-20 h-20 object-cover object-top rounded-md"
                        />
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900 mb-1 text-sm">{item.title}</h3>
                          <p className="text-xs text-gray-500 mb-2">{item.variantTitle}</p>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center border border-gray-300 rounded">
                              <button
                                onClick={() => updateCartItemQuantity(item.id, item.quantity - 1)}
                                className="px-2 py-1 hover:bg-gray-100 cursor-pointer"
                              >
                                <i className="ri-subtract-line w-3 h-3 flex items-center justify-center"></i>
                              </button>
                              <span className="px-3 py-1 text-sm font-semibold">{item.quantity}</span>
                              <button
                                onClick={() => updateCartItemQuantity(item.id, item.quantity + 1)}
                                className="px-2 py-1 hover:bg-gray-100 cursor-pointer"
                              >
                                <i className="ri-add-line w-3 h-3 flex items-center justify-center"></i>
                              </button>
                            </div>
                            <span className="font-bold text-cyan-600 text-sm">
                              ${(item.price * item.quantity).toFixed(2)}
                            </span>
                          </div>
                        </div>
                        <button
                          onClick={() => removeCartItem(item.id)}
                          className="text-red-500 hover:text-red-700 cursor-pointer"
                        >
                          <i className="ri-delete-bin-line text-lg w-5 h-5 flex items-center justify-center"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Cart Footer */}
              {cartItems.length > 0 && (
                <div className="border-t border-gray-200 p-6 space-y-4">
                  <div className="flex items-center justify-between text-lg font-semibold">
                    <span className="text-gray-900 uppercase tracking-wider">Total:</span>
                    <span className="text-cyan-600">
                      ${cartTotal.toFixed(2)}
                    </span>
                  </div>
                  
                  <button className="w-full bg-cyan-600 text-white py-3 rounded-md hover:bg-cyan-700 transition-colors cursor-pointer font-semibold whitespace-nowrap text-sm uppercase tracking-wider">
                    Proceed to Checkout
                  </button>
                  
                  <button
                    onClick={clearCart}
                    className="w-full border border-gray-300 text-gray-700 py-3 rounded-md hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap text-sm uppercase tracking-wider"
                  >
                    Clear Cart
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </header>
    </>
  );
}
